import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import encodeListFilterValues, { readListFilterValues } from "~/helpers/listFilterValues";
import { INVENTORY_DOCK_PAGE_ID, ORDERS_DOCK_PAGE_ID, PRODUCTS_ITEMS_DOCK_PAGE_ID } from "~/helpers/panelKey";
import deviceReceiverIdentityAdapter, {
	DEVICE_TARGET_KIND,
	DEVICES_DOCK_PANEL_ID
} from "~admin/devices/helpers/deviceContinuation";
import emailSenderReceiverIdentityAdapter, {
	EMAIL_SENDER_TARGET_KIND,
	EMAIL_SETTINGS_DOCK_PAGE_ID,
	NEW_EMAIL_SENDER_TARGET_KIND,
	newEmailSenderReceiverIdentityAdapter
} from "~admin/email/helpers/senderReceiverIdentity";
import lineReceiverIdentityAdapter, {
	LINE_TARGET_KIND,
	LINES_DOCK_PANEL_ID
} from "~count/lines/helpers/lineContinuation";
import { CUSTOMER_ORDERS_TARGET_KIND } from "~fulfill/customers/customer/health/helpers/customerHealthOrders";
import customerReceiverIdentityAdapter, {
	CUSTOMER_TARGET_KIND,
	NEW_CUSTOMER_TARGET_KIND,
	newCustomerReceiverIdentityAdapter
} from "~fulfill/customers/customer/helpers/customerContinuation";
import customerGroupMembersReceiverIdentityAdapter, {
	CUSTOMER_GROUP_MEMBERS_TARGET_KIND,
	CUSTOMERS_DOCK_PANEL_ID
} from "~fulfill/customers/customer/helpers/customerGroupMembersScope";
import customerListScope from "~fulfill/customers/customer/helpers/customerListScope";
import STOCK_LOT_TARGET_KIND from "~fulfill/inventory/stock/constants/stockLotTargetKind";
import hardgoodStockReceiverIdentityAdapter, {
	HARDGOOD_STOCK_TARGET_KIND
} from "~fulfill/inventory/stock/helpers/hardgoodStockScope";
import stockLotReceiverIdentity from "~fulfill/inventory/stock/helpers/stockLotReceiverIdentity";
import locationReceiverIdentity from "~fulfill/locations/helpers/locationReceiverIdentity";
import siteListScope from "~fulfill/locations/helpers/siteListScope";
import {
	MASTER_ORDER_TARGET_KIND,
	masterOrderReceiverIdentityAdapter
} from "~fulfill/orders/helpers/masterOrderContinuation";
import { ORDER_TARGET_KIND, orderReceiverIdentityAdapter } from "~fulfill/orders/helpers/orderContinuation";
import parseItemsRoute, {
	buildItemsRoute,
	CREATE_ITEM_ROUTE_SEGMENT
} from "~fulfill/product/items/helpers/itemsRoutes";
import kitReceiverIdentityAdapter, {
	KIT_TARGET_KIND,
	KITS_DOCK_PANEL_ID,
	NEW_KIT_TARGET_KIND
} from "~fulfill/product/kits/helpers/kitContinuation";
import invoiceReceiverIdentityAdapter, {
	INVOICE_TARGET_KIND,
	INVOICES_DOCK_PAGE_ID
} from "~fulfill/sales/invoices/helpers/invoiceContinuation";
import {
	PRICE_GROUP_TARGET_KIND,
	PRICE_GROUPS_DOCK_PANEL_ID,
	PRICE_LEVEL_TARGET_KIND,
	priceGroupReceiverIdentityAdapter,
	priceLevelReceiverIdentityAdapter
} from "~fulfill/sales/pricing/helpers/priceGroupContinuation";
import availabilityHistoryReceiverIdentityAdapter, {
	AVAILABILITY_HISTORY_RAIL_ID,
	AVAILABILITY_HISTORY_TARGET_KIND
} from "~mobile/availability/history/helpers/availabilityHistoryTarget";
import engageHistoryReceiverIdentityAdapter, {
	ENGAGE_HISTORY_RAIL_ID,
	ENGAGE_HISTORY_TARGET_KIND
} from "~mobile/engage/history/helpers/engageHistoryTarget";
import eventsJobReceiverIdentityAdapter, {
	EVENTS_JOB_TARGET_KIND,
	EVENTS_JOBS_RAIL_ID
} from "~mobile/events/helpers/eventsJobTarget";
import {
	EVENTS_TASK_TARGET_KIND,
	EVENTS_TASKS_RAIL_ID,
	eventsTaskReceiverIdentityAdapter
} from "~mobile/events/helpers/eventsTimeContinuation";
import materialReceivingReceiverIdentityAdapter, {
	MATERIAL_RECEIVING_RAIL_ID,
	MATERIAL_RECEIVING_TARGET_KIND
} from "~mobile/material/receive/helpers/materialReceivingTarget";
import overheadRateHistoryReceiverIdentityAdapter, {
	OVERHEAD_RATE_HISTORY_TARGET_KIND,
	OVERHEAD_RATES_DOCK_PANEL_ID
} from "~produce/capacity/overheadRates/helpers/overheadRatesContinuation";
import hardgoodsInventoryReceiverIdentityAdapter, {
	HARDGOODS_INVENTORY_DOCK_PANEL_ID,
	HARDGOODS_INVENTORY_TARGET_KIND
} from "~produce/inventory/hardgoods/helpers/hardgoodsExactScope";
import {
	LABOR_RATE_HISTORY_TARGET_KIND,
	LABOR_RATES_DOCK_PANEL_ID,
	laborRateHistoryReceiverIdentityAdapter
} from "~produce/labor/rates/helpers/laborRatesContinuation";
import {
	LABOR_SCHEDULE_DOCK_PANEL_ID,
	LABOR_WORKER_TARGET_KIND,
	laborWorkerReceiverIdentityAdapter
} from "~produce/labor/schedule/helpers/laborScheduleContinuation";
import {
	LABOR_TASK_LIST_TARGET_KIND,
	LABOR_TASK_TARGET_KIND,
	LABOR_TASKS_DOCK_PANEL_ID,
	laborTaskListReceiverIdentityAdapter,
	laborTaskReceiverIdentityAdapter
} from "~produce/labor/tasks/helpers/laborTasksContinuation";
import eventDetailReceiverIdentityAdapter, {
	EVENT_DETAIL_TARGET_KINDS,
	MATERIAL_EVENT_TARGET_KIND,
	materialEventReceiverIdentityAdapter,
	SPACE_EVENT_TARGET_KIND,
	spaceEventReceiverIdentityAdapter
} from "~produce/production/events/helpers/eventReceiverIdentity";
import taskEventReceiverIdentityAdapter, {
	EVENTS_DOCK_PANEL_ID,
	TASK_EVENT_LIST_TARGET_KIND,
	TASK_EVENT_TARGET_KIND,
	taskEventListReceiverIdentityAdapter
} from "~produce/production/events/helpers/taskEventContinuation";
import recipeReceiverIdentityAdapter, {
	LINKING_RECIPES_TARGET_KIND,
	linkingRecipesReceiverIdentityAdapter,
	RECIPE_TARGET_KIND,
	RECIPES_DOCK_PANEL_ID
} from "~produce/production/helpers/recipeReceiverIdentity";
import materialReceiverIdentity from "~produce/production/materialItems/helpers/materialReceiverIdentity";
import eventJobReceiverIdentityAdapter, {
	EVENT_JOB_TARGET_KIND
} from "~produce/production/schedule/events/helpers/eventJobScope";
import eventWeekReceiverIdentityAdapter, {
	EVENT_SCHEDULE_DOCK_PANEL_ID,
	EVENT_WEEK_TARGET_KIND
} from "~produce/production/schedule/events/helpers/eventWeekScope";
import purchaseOrderReceiverIdentityAdapter, {
	FULFILL_PURCHASE_ORDER_TARGET_KIND,
	FULFILL_PURCHASE_ORDERS_DOCK_PAGE_ID,
	PURCHASE_ORDER_TARGET_KIND,
	PURCHASE_ORDERS_DOCK_PAGE_ID
} from "~produce/purchasing/orders/helpers/purchaseOrderReceiverIdentity";
import vendorPurchaseOrdersReceiverIdentityAdapter, {
	VENDOR_PURCHASE_ORDERS_TARGET_KIND
} from "~produce/purchasing/orders/helpers/vendorPurchaseOrdersReceiverIdentity";
import vendorReceiverIdentityAdapter, {
	FULFILL_VENDOR_TARGET_KIND,
	FULFILL_VENDORS_DOCK_PAGE_ID,
	fulfillVendorReceiverIdentityAdapter,
	VENDOR_TARGET_KIND,
	VENDORS_DOCK_PAGE_ID
} from "~produce/purchasing/vendors/helpers/vendorReceiverIdentity";
import { REPORT_TARGET_KIND, reportReceiverIdentityAdapter } from "~reports/builder/helpers/reportBuilderContinuation";
import {
	EMAIL_SEND_TARGET_KIND,
	emailSendReceiverIdentityAdapter
} from "~reports/emailIssues/helpers/emailSendContinuation";
import {
	REPORT_RUN_TARGET_KIND,
	reportRunReceiverIdentityAdapter
} from "~reports/executions/helpers/reportRunContinuation";
import {
	REPORT_SCHEDULE_TARGET_KIND,
	REPORTS_DOCK_PAGE_ID,
	reportScheduleReceiverIdentityAdapter
} from "~reports/schedules/helpers/reportScheduleContinuation";
import restockItemReceiverIdentityAdapter, {
	NEW_RESTOCK_ITEM_TARGET_KIND,
	newRestockItemReceiverIdentityAdapter,
	RESTOCK_ITEM_TARGET_KIND,
	RESTOCK_ITEMS_DOCK_PAGE_ID
} from "~restock/data-management/items/helpers/restockItemReceiverIdentity";
import restockStoreReceiverIdentityAdapter, {
	NEW_RESTOCK_STORE_TARGET_KIND,
	newRestockStoreReceiverIdentityAdapter,
	RESTOCK_LOCATIONS_DOCK_PAGE_ID,
	RESTOCK_STORE_TARGET_KIND
} from "~restock/data-management/locations/helpers/restockStoreReceiverIdentity";
import storeListScope from "~restock/data-management/locations/helpers/storeListScope";
import {
	RESTOCK_RULE_TARGET_KIND,
	RESTOCK_RULES_DOCK_PAGE_ID,
	restockRuleReceiverIdentityAdapter
} from "~restock/data-management/rules/helpers/ruleContinuation";

export const CATALOG_TARGET_KIND = "productionCatalog";

export const CATEGORY_TARGET_KIND = "productionCategory";

export const SALES_ITEM_TARGET_KIND = "salesItem";

export const NEW_SALES_ITEM_TARGET_KIND = "newSalesItem";

export const SALES_ITEMS_LIST_TARGET_KIND = "salesItemsList";

export const PRODUCTION_ITEM_TARGET_KIND = "productionItem";

export const PRODUCTION_JOB_TARGET_KIND = "productionJob";

export const NEW_PRODUCTION_JOB_TARGET_KIND = "newProductionJob";

export const SCHEDULED_JOBS_TARGET_KIND = "scheduledJobs";

export const SCHEDULE_JOB_LIST_TARGET_KIND = "scheduleJobList";

export const PRODUCTION_ITEMS_BASE_PATH = "/produce/production/items";

export const PRODUCTION_ITEMS_DOCK_PANEL_ID = "production-items";

export const PRODUCTION_SCHEDULE_BASE_PATH = "/produce/production/schedule";

export const PRODUCTION_SCHEDULE_DOCK_PANEL_ID = "production-schedule";

export const CATALOGS_BASE_PATH = `${PRODUCTION_ITEMS_BASE_PATH}/catalogs`;

export const CATEGORIES_BASE_PATH = `${PRODUCTION_ITEMS_BASE_PATH}/categories`;

const PRODUCTION_ITEMS_TAB_PATH = `${PRODUCTION_ITEMS_BASE_PATH}/production-items`;

const RESERVED_SETUP_SEGMENTS = ["import"];

const SALES_ITEM_ROUTE_PATTERN = /^\/fulfill\/product\/items\/([^/?#]+)$/;

const PRODUCTION_ITEM_ROUTE_PATTERN = /^\/produce\/production\/items\/production-items\/([^/?#]+)$/;

const PRODUCTION_JOB_ROUTE_PATTERN = /^\/produce\/production\/schedule\/jobs\/([^/?#]+)$/;

const matchSetupRoute = (pathname: string, pattern: RegExp, targetKind: string): ContinuationTarget | null => {
	const match = pattern.exec(pathname);
	if (!match) return null;
	const targetId = decodeURIComponent(match[1]);
	if (RESERVED_SETUP_SEGMENTS.includes(targetId)) return null;
	return { targetId, targetKind };
};

export const matchSalesItemReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = SALES_ITEM_ROUTE_PATTERN.exec(pathname);
	const route = match ? parseItemsRoute(decodeURIComponent(match[1])) : null;
	return route?.mode === "detail" ? { targetId: route.itemId, targetKind: SALES_ITEM_TARGET_KIND } : null;
};

export const matchProductionItemReceiverRoute = (pathname: string): ContinuationTarget | null =>
	matchSetupRoute(pathname, PRODUCTION_ITEM_ROUTE_PATTERN, PRODUCTION_ITEM_TARGET_KIND);

export const isProductionItemsRoute = (pathname: string) =>
	pathname === PRODUCTION_ITEMS_TAB_PATH || matchProductionItemReceiverRoute(pathname) !== null;

export const matchProductionJobReceiverRoute = (pathname: string): ContinuationTarget | null =>
	matchSetupRoute(pathname, PRODUCTION_JOB_ROUTE_PATTERN, PRODUCTION_JOB_TARGET_KIND);

export const isProductionScheduleRoute = (pathname: string) =>
	pathname === PRODUCTION_SCHEDULE_BASE_PATH || matchProductionJobReceiverRoute(pathname) !== null;

export const salesItemReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildItemsRoute({ itemId: encodeURIComponent(targetId), mode: "detail" }),
	matchesRoute: matchSalesItemReceiverRoute
};

export const NEW_SALES_ITEM_TARGET: ContinuationTarget = {
	targetId: CREATE_ITEM_ROUTE_SEGMENT,
	targetKind: NEW_SALES_ITEM_TARGET_KIND
};

export const NEW_SALES_ITEM_CODE_FILTER = "itemCode";

export const newSalesItemTargetWithCode = (itemCode: string): ContinuationTarget => {
	const code = itemCode.trim();
	return code
		? { ...NEW_SALES_ITEM_TARGET, listFilters: { [NEW_SALES_ITEM_CODE_FILTER]: code } }
		: NEW_SALES_ITEM_TARGET;
};

export const readNewSalesItemCode = (listFilters: Record<string, string> | undefined): null | string =>
	listFilters?.[NEW_SALES_ITEM_CODE_FILTER]?.trim() || null;

export const matchNewSalesItemReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = SALES_ITEM_ROUTE_PATTERN.exec(pathname);
	const route = match ? parseItemsRoute(decodeURIComponent(match[1])) : null;
	return route?.mode === "create" ? NEW_SALES_ITEM_TARGET : null;
};

export const newSalesItemReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: () => buildItemsRoute({ mode: "create" }),
	matchesRoute: matchNewSalesItemReceiverRoute
};

export const SALES_ITEMS_LIST_TARGET: ContinuationTarget = {
	targetId: "list",
	targetKind: SALES_ITEMS_LIST_TARGET_KIND
};

export const matchSalesItemsListReceiverRoute = (pathname: string): ContinuationTarget | null =>
	pathname === buildItemsRoute({ mode: "list" }) ? SALES_ITEMS_LIST_TARGET : null;

export const salesItemsListReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: () => buildItemsRoute({ mode: "list" }),
	matchesRoute: matchSalesItemsListReceiverRoute
};

export const buildProductionItemSourcePath = (productionItemId: string): string =>
	productionItemId ? `${PRODUCTION_ITEMS_TAB_PATH}/${encodeURIComponent(productionItemId)}` : PRODUCTION_ITEMS_TAB_PATH;

export const productionItemReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildProductionItemSourcePath(targetId),
	matchesRoute: matchProductionItemReceiverRoute
};

export const productionJobReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${PRODUCTION_SCHEDULE_BASE_PATH}/jobs/${encodeURIComponent(targetId)}`,
	matchesRoute: matchProductionJobReceiverRoute
};

export const NEW_PRODUCTION_JOB_TARGET: ContinuationTarget = {
	targetId: "new",
	targetKind: NEW_PRODUCTION_JOB_TARGET_KIND
};

export const newProductionJobReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: () => PRODUCTION_SCHEDULE_BASE_PATH,
	matchesRoute: (pathname) => (pathname === PRODUCTION_SCHEDULE_BASE_PATH ? NEW_PRODUCTION_JOB_TARGET : null)
};

export interface ScheduledJobsSource {
	catalogColor: string;
	catalogGenus: string;
	catalogSeries: string;
	categoryCode: string;
	productionItemId: string;
}

export const buildScheduledJobsTarget = (productionItem: ScheduledJobsSource): ContinuationTarget => ({
	listFilters: {
		category: productionItem.categoryCode,
		color: productionItem.catalogColor,
		genus: productionItem.catalogGenus,
		series: productionItem.catalogSeries
	},
	targetId: productionItem.productionItemId,
	targetKind: SCHEDULED_JOBS_TARGET_KIND
});

export const buildPlanScheduledJobsTarget = (productionPlanId: string): ContinuationTarget => ({
	listFilters: { productionPlanId },
	targetId: productionPlanId,
	targetKind: SCHEDULED_JOBS_TARGET_KIND
});

export const scheduledJobsReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: () => PRODUCTION_SCHEDULE_BASE_PATH,
	matchesRoute: () => null
};

export const buildScheduleJobListTarget = (
	listId: string,
	productionJobIds: readonly string[]
): ContinuationTarget => ({
	listFilters: { productionJobIds: encodeListFilterValues(productionJobIds) },
	targetId: listId,
	targetKind: SCHEDULE_JOB_LIST_TARGET_KIND
});

export const readScheduleJobListIds = (listFilters: Record<string, string> | undefined): null | string[] =>
	readListFilterValues(listFilters?.productionJobIds);

export const receiverPanelIdForTargetKind = (targetKind: string): string => {
	if (targetKind === KIT_TARGET_KIND || targetKind === NEW_KIT_TARGET_KIND) return KITS_DOCK_PANEL_ID;
	if (targetKind === RECIPE_TARGET_KIND || targetKind === LINKING_RECIPES_TARGET_KIND) return RECIPES_DOCK_PANEL_ID;
	if (
		targetKind === SALES_ITEM_TARGET_KIND ||
		targetKind === NEW_SALES_ITEM_TARGET_KIND ||
		targetKind === SALES_ITEMS_LIST_TARGET_KIND
	)
		return PRODUCTS_ITEMS_DOCK_PAGE_ID;
	if (
		targetKind === PRODUCTION_JOB_TARGET_KIND ||
		targetKind === NEW_PRODUCTION_JOB_TARGET_KIND ||
		targetKind === SCHEDULED_JOBS_TARGET_KIND ||
		targetKind === SCHEDULE_JOB_LIST_TARGET_KIND
	)
		return PRODUCTION_SCHEDULE_DOCK_PANEL_ID;
	if (targetKind === HARDGOODS_INVENTORY_TARGET_KIND) return HARDGOODS_INVENTORY_DOCK_PANEL_ID;
	if (targetKind === EVENT_WEEK_TARGET_KIND || targetKind === EVENT_JOB_TARGET_KIND)
		return EVENT_SCHEDULE_DOCK_PANEL_ID;
	if (targetKind === EMAIL_SENDER_TARGET_KIND || targetKind === NEW_EMAIL_SENDER_TARGET_KIND)
		return EMAIL_SETTINGS_DOCK_PAGE_ID;
	if (
		targetKind === CUSTOMER_GROUP_MEMBERS_TARGET_KIND ||
		targetKind === CUSTOMER_TARGET_KIND ||
		targetKind === NEW_CUSTOMER_TARGET_KIND ||
		targetKind === customerListScope.CUSTOMER_LIST_TARGET_KIND
	)
		return CUSTOMERS_DOCK_PANEL_ID;
	if (targetKind === VENDOR_TARGET_KIND) return VENDORS_DOCK_PAGE_ID;
	if (targetKind === FULFILL_VENDOR_TARGET_KIND) return FULFILL_VENDORS_DOCK_PAGE_ID;
	if (targetKind === PURCHASE_ORDER_TARGET_KIND || targetKind === VENDOR_PURCHASE_ORDERS_TARGET_KIND)
		return PURCHASE_ORDERS_DOCK_PAGE_ID;
	if (targetKind === FULFILL_PURCHASE_ORDER_TARGET_KIND) return FULFILL_PURCHASE_ORDERS_DOCK_PAGE_ID;
	if (targetKind === materialReceiverIdentity.MATERIAL_ITEM_TARGET_KIND)
		return materialReceiverIdentity.MATERIAL_ITEMS_DOCK_PAGE_ID;
	if (
		targetKind === locationReceiverIdentity.SITE_LOCATION_TARGET_KIND ||
		targetKind === locationReceiverIdentity.BIN_LOCATION_TARGET_KIND
	)
		return locationReceiverIdentity.CAPACITY_LOCATIONS_DOCK_PAGE_ID;
	if (targetKind === siteListScope.SITE_LIST_TARGET_KIND) return siteListScope.FULFILL_LOCATIONS_DOCK_PAGE_ID;
	if (targetKind === PRICE_GROUP_TARGET_KIND || targetKind === PRICE_LEVEL_TARGET_KIND)
		return PRICE_GROUPS_DOCK_PANEL_ID;
	if (targetKind === AVAILABILITY_HISTORY_TARGET_KIND) return AVAILABILITY_HISTORY_RAIL_ID;
	if (targetKind === ENGAGE_HISTORY_TARGET_KIND) return ENGAGE_HISTORY_RAIL_ID;
	if (targetKind === MATERIAL_RECEIVING_TARGET_KIND) return MATERIAL_RECEIVING_RAIL_ID;
	if (targetKind === LABOR_WORKER_TARGET_KIND) return LABOR_SCHEDULE_DOCK_PANEL_ID;
	if (EVENT_DETAIL_TARGET_KINDS.includes(targetKind)) return EVENTS_DOCK_PANEL_ID;
	if (targetKind === LABOR_TASK_TARGET_KIND || targetKind === LABOR_TASK_LIST_TARGET_KIND)
		return LABOR_TASKS_DOCK_PANEL_ID;
	if (targetKind === LINE_TARGET_KIND) return LINES_DOCK_PANEL_ID;
	if (targetKind === DEVICE_TARGET_KIND) return DEVICES_DOCK_PANEL_ID;
	if (
		targetKind === REPORT_SCHEDULE_TARGET_KIND ||
		targetKind === REPORT_TARGET_KIND ||
		targetKind === REPORT_RUN_TARGET_KIND ||
		targetKind === EMAIL_SEND_TARGET_KIND
	)
		return REPORTS_DOCK_PAGE_ID;
	if (
		targetKind === ORDER_TARGET_KIND ||
		targetKind === CUSTOMER_ORDERS_TARGET_KIND ||
		targetKind === MASTER_ORDER_TARGET_KIND
	)
		return ORDERS_DOCK_PAGE_ID;
	if (targetKind === INVOICE_TARGET_KIND) return INVOICES_DOCK_PAGE_ID;
	if (targetKind === RESTOCK_ITEM_TARGET_KIND || targetKind === NEW_RESTOCK_ITEM_TARGET_KIND)
		return RESTOCK_ITEMS_DOCK_PAGE_ID;
	if (
		targetKind === RESTOCK_STORE_TARGET_KIND ||
		targetKind === NEW_RESTOCK_STORE_TARGET_KIND ||
		targetKind === storeListScope.STORE_LIST_TARGET_KIND
	)
		return RESTOCK_LOCATIONS_DOCK_PAGE_ID;
	if (targetKind === RESTOCK_RULE_TARGET_KIND) return RESTOCK_RULES_DOCK_PAGE_ID;
	if (targetKind === EVENTS_TASK_TARGET_KIND) return EVENTS_TASKS_RAIL_ID;
	if (targetKind === LABOR_RATE_HISTORY_TARGET_KIND) return LABOR_RATES_DOCK_PANEL_ID;
	if (targetKind === OVERHEAD_RATE_HISTORY_TARGET_KIND) return OVERHEAD_RATES_DOCK_PANEL_ID;
	if (
		targetKind === TASK_EVENT_TARGET_KIND ||
		targetKind === TASK_EVENT_LIST_TARGET_KIND ||
		targetKind === MATERIAL_EVENT_TARGET_KIND ||
		targetKind === SPACE_EVENT_TARGET_KIND
	)
		return EVENTS_DOCK_PANEL_ID;
	if (targetKind === EVENTS_JOB_TARGET_KIND) return EVENTS_JOBS_RAIL_ID;
	if (targetKind === HARDGOOD_STOCK_TARGET_KIND || targetKind === STOCK_LOT_TARGET_KIND) return INVENTORY_DOCK_PAGE_ID;
	return PRODUCTION_ITEMS_DOCK_PANEL_ID;
};

export const receiverIdentityAdapterForTargetKind = (targetKind: string): null | ReceiverIdentityAdapter => {
	if (targetKind === KIT_TARGET_KIND || targetKind === NEW_KIT_TARGET_KIND) return kitReceiverIdentityAdapter;
	if (targetKind === STOCK_LOT_TARGET_KIND) return stockLotReceiverIdentity;
	if (targetKind === RECIPE_TARGET_KIND) return recipeReceiverIdentityAdapter;
	if (targetKind === LINKING_RECIPES_TARGET_KIND) return linkingRecipesReceiverIdentityAdapter;
	if (targetKind === SALES_ITEM_TARGET_KIND) return salesItemReceiverIdentityAdapter;
	if (targetKind === NEW_SALES_ITEM_TARGET_KIND) return newSalesItemReceiverIdentityAdapter;
	if (targetKind === PRODUCTION_ITEM_TARGET_KIND) return productionItemReceiverIdentityAdapter;
	if (targetKind === PRODUCTION_JOB_TARGET_KIND) return productionJobReceiverIdentityAdapter;
	if (targetKind === NEW_PRODUCTION_JOB_TARGET_KIND) return newProductionJobReceiverIdentityAdapter;
	if (targetKind === SCHEDULED_JOBS_TARGET_KIND || targetKind === SCHEDULE_JOB_LIST_TARGET_KIND)
		return scheduledJobsReceiverIdentityAdapter;
	if (targetKind === HARDGOODS_INVENTORY_TARGET_KIND) return hardgoodsInventoryReceiverIdentityAdapter;
	if (targetKind === EVENT_WEEK_TARGET_KIND) return eventWeekReceiverIdentityAdapter;
	if (targetKind === EVENT_JOB_TARGET_KIND) return eventJobReceiverIdentityAdapter;
	if (targetKind === EMAIL_SENDER_TARGET_KIND) return emailSenderReceiverIdentityAdapter;
	if (targetKind === NEW_EMAIL_SENDER_TARGET_KIND) return newEmailSenderReceiverIdentityAdapter;
	if (targetKind === CUSTOMER_GROUP_MEMBERS_TARGET_KIND) return customerGroupMembersReceiverIdentityAdapter;
	if (targetKind === CUSTOMER_TARGET_KIND) return customerReceiverIdentityAdapter;
	if (targetKind === NEW_CUSTOMER_TARGET_KIND) return newCustomerReceiverIdentityAdapter;
	if (targetKind === customerListScope.CUSTOMER_LIST_TARGET_KIND) return customerListScope.adapter;
	if (targetKind === VENDOR_TARGET_KIND) return vendorReceiverIdentityAdapter;
	if (targetKind === FULFILL_VENDOR_TARGET_KIND) return fulfillVendorReceiverIdentityAdapter;
	if (targetKind === PURCHASE_ORDER_TARGET_KIND || targetKind === FULFILL_PURCHASE_ORDER_TARGET_KIND)
		return purchaseOrderReceiverIdentityAdapter;
	if (targetKind === VENDOR_PURCHASE_ORDERS_TARGET_KIND) return vendorPurchaseOrdersReceiverIdentityAdapter;
	if (targetKind === materialReceiverIdentity.MATERIAL_ITEM_TARGET_KIND) return materialReceiverIdentity.adapter;
	if (
		targetKind === locationReceiverIdentity.SITE_LOCATION_TARGET_KIND ||
		targetKind === locationReceiverIdentity.BIN_LOCATION_TARGET_KIND
	)
		return locationReceiverIdentity.adapter;
	if (targetKind === siteListScope.SITE_LIST_TARGET_KIND) return siteListScope.adapter;
	if (targetKind === PRICE_GROUP_TARGET_KIND) return priceGroupReceiverIdentityAdapter;
	if (targetKind === PRICE_LEVEL_TARGET_KIND) return priceLevelReceiverIdentityAdapter;
	if (targetKind === SALES_ITEMS_LIST_TARGET_KIND) return salesItemsListReceiverIdentityAdapter;
	if (targetKind === AVAILABILITY_HISTORY_TARGET_KIND) return availabilityHistoryReceiverIdentityAdapter;
	if (targetKind === ENGAGE_HISTORY_TARGET_KIND) return engageHistoryReceiverIdentityAdapter;
	if (targetKind === MATERIAL_RECEIVING_TARGET_KIND) return materialReceivingReceiverIdentityAdapter;
	if (targetKind === LABOR_WORKER_TARGET_KIND) return laborWorkerReceiverIdentityAdapter;
	if (EVENT_DETAIL_TARGET_KINDS.includes(targetKind)) return eventDetailReceiverIdentityAdapter;
	if (targetKind === LABOR_TASK_TARGET_KIND) return laborTaskReceiverIdentityAdapter;
	if (targetKind === LABOR_TASK_LIST_TARGET_KIND) return laborTaskListReceiverIdentityAdapter;
	if (targetKind === LINE_TARGET_KIND) return lineReceiverIdentityAdapter;
	if (targetKind === DEVICE_TARGET_KIND) return deviceReceiverIdentityAdapter;
	if (targetKind === REPORT_SCHEDULE_TARGET_KIND) return reportScheduleReceiverIdentityAdapter;
	if (targetKind === RESTOCK_ITEM_TARGET_KIND) return restockItemReceiverIdentityAdapter;
	if (targetKind === NEW_RESTOCK_ITEM_TARGET_KIND) return newRestockItemReceiverIdentityAdapter;
	if (targetKind === RESTOCK_STORE_TARGET_KIND) return restockStoreReceiverIdentityAdapter;
	if (targetKind === NEW_RESTOCK_STORE_TARGET_KIND) return newRestockStoreReceiverIdentityAdapter;
	if (targetKind === storeListScope.STORE_LIST_TARGET_KIND) return storeListScope.adapter;
	if (targetKind === RESTOCK_RULE_TARGET_KIND) return restockRuleReceiverIdentityAdapter;
	if (targetKind === EVENTS_TASK_TARGET_KIND) return eventsTaskReceiverIdentityAdapter;
	if (targetKind === LABOR_RATE_HISTORY_TARGET_KIND) return laborRateHistoryReceiverIdentityAdapter;
	if (targetKind === OVERHEAD_RATE_HISTORY_TARGET_KIND) return overheadRateHistoryReceiverIdentityAdapter;
	if (targetKind === TASK_EVENT_TARGET_KIND) return taskEventReceiverIdentityAdapter;
	if (targetKind === TASK_EVENT_LIST_TARGET_KIND) return taskEventListReceiverIdentityAdapter;
	if (targetKind === MATERIAL_EVENT_TARGET_KIND) return materialEventReceiverIdentityAdapter;
	if (targetKind === SPACE_EVENT_TARGET_KIND) return spaceEventReceiverIdentityAdapter;
	if (targetKind === EVENTS_JOB_TARGET_KIND) return eventsJobReceiverIdentityAdapter;
	if (targetKind === HARDGOOD_STOCK_TARGET_KIND) return hardgoodStockReceiverIdentityAdapter;
	if (targetKind === REPORT_TARGET_KIND) return reportReceiverIdentityAdapter;
	if (targetKind === REPORT_RUN_TARGET_KIND) return reportRunReceiverIdentityAdapter;
	if (targetKind === EMAIL_SEND_TARGET_KIND) return emailSendReceiverIdentityAdapter;
	if (targetKind === MASTER_ORDER_TARGET_KIND) return masterOrderReceiverIdentityAdapter;
	if (targetKind === CUSTOMER_ORDERS_TARGET_KIND)
		return { buildCleanPath: () => "/fulfill/orders", matchesRoute: () => null };
	if (targetKind === ORDER_TARGET_KIND) return orderReceiverIdentityAdapter;
	if (targetKind === INVOICE_TARGET_KIND) return invoiceReceiverIdentityAdapter;
	return null;
};
